import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SpaceMonkeyLogo from '../components/brand/SpaceMonkeyLogo';
import Logo from '../components/brand/Logo';
import Icon from '../components/brand/Icon';
import parkingApi from '../services/parkingApi';
import './EnforcementPage.css';

// The enforcement console currently serves the Space Monkey lot.
const LOT_SLUG = 'spacemonkey';

const STATUS_CONFIG = {
  VALID:     { label: 'VALID PERMIT',       color: 'green',  icon: 'check', action: 'Valid permit: No action needed.' },
  VALIDATED: { label: 'VALIDATED CUSTOMER', color: 'blue',   icon: 'check', action: 'Validated customer: No action needed.' },
  EXPIRED:   { label: 'EXPIRED',            color: 'yellow', icon: 'alert', action: 'Expired: Ask driver to renew or proceed with lot policy.' },
  NO_PERMIT: { label: 'NO PERMIT FOUND',    color: 'red',    icon: 'ban',   action: 'No permit found: Ask driver to pay or proceed with lot policy.' },
};

const lookupPlate = async (raw) => {
  const plate = raw.trim().toUpperCase().replace(/\s+/g, '');
  return parkingApi.lookupPlate({ slug: LOT_SLUG, plate });
};

const EnforcementPage = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('authToken')) {
      navigate('/admin/signin');
    }
  }, [navigate]);

  // Live lot figures. Refreshes after each lookup so the counts stay current.
  const loadStats = async () => {
    try {
      const s = await parkingApi.lotSummary(LOT_SLUG);
      setStats([
        { label: 'Active Permits', value: s.activePermits, color: 'green' },
        { label: 'Validated Customers', value: s.validatedCustomers, color: 'blue' },
        { label: 'Expired Permits', value: s.expiredPermits, color: 'yellow' },
        { label: "Today's Revenue", value: s.todaysRevenue, color: 'teal' },
      ]);
    } catch {
      setStats(null);
    }
  };

  useEffect(() => { loadStats(); }, []);

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setSearched(false);
    try {
      const data = await lookupPlate(query);
      setResult(data);
      loadStats();
    } catch {
      setResult({ status: 'NO_PERMIT', plate: query.toUpperCase() });
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const cfg = result ? STATUS_CONFIG[result.status] : null;

  return (
    <div className="enf-page">
      <header className="enf-header">
        <div className="enf-header-inner">
          <Link to="/spacemonkey/dashboard" className="enf-brand">
            <SpaceMonkeyLogo size="sm" tagline={false} />
            <span className="enf-brand-label">Enforcement</span>
          </Link>
          <div className="enf-header-right">
            <span className="enf-powered"><span>Powered by</span> <Logo size="sm" /></span>
            <Link to="/enforcement/scan" className="enf-scan-link">
              <Icon name="camera" size={16} /> Scan Plate
            </Link>
          </div>
        </div>
      </header>

      <div className="enf-content">
        {stats && (
          <div className="enf-stats">
            {stats.map(s => (
              <div className={`enf-stat enf-stat-${s.color}`} key={s.label}>
                <div className="enf-stat-value">{s.value}</div>
                <div className="enf-stat-label">{s.label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="enf-search-section">
          <h1>License Plate Lookup</h1>
          <p>Enter a plate number to verify permit status. Always confirm plate status here before applying any lot policy.</p>

          <form onSubmit={handleSearch} className="enf-search-form">
            <input
              className="enf-search-input"
              placeholder="Enter plate (e.g. ABC1234)"
              value={query}
              onChange={e => setQuery(e.target.value.toUpperCase())}
              maxLength={10}
              autoCapitalize="characters"
              autoFocus
            />
            <button type="submit" className="enf-search-btn" disabled={loading || !query.trim()}>
              {loading ? <span className="enf-spinner"></span> : 'Look Up'}
            </button>
          </form>
        </div>

        {searched && result && cfg && (
          <div className={`enf-result enf-result-${cfg.color}`}>
            <div className="enf-result-icon"><Icon name={cfg.icon} size={34} /></div>
            <div className="enf-result-status">{cfg.label}</div>
            <div className="enf-result-plate">{result.plate}{result.state ? ` · ${result.state}` : ''}</div>
            <div className="enf-result-action">{cfg.action}</div>

            {(result.permitId || result.paidAt) && (
              <div className="enf-result-details">
                {result.permitId && <div><span>Permit ID</span><strong>{result.permitId}</strong></div>}
                {result.paidAt && <div><span>Purchased</span><strong>{result.paidAt}</strong></div>}
                {result.expiresAt && <div><span>Expires</span><strong>{result.expiresAt}</strong></div>}
                {result.amount && <div><span>Amount Paid</span><strong>{result.amount}</strong></div>}
              </div>
            )}

            {result.code && (
              <div className="enf-result-details">
                <div><span>Validation Code</span><strong>{result.code}</strong></div>
                {result.validatedAt && <div><span>Validated At</span><strong>{result.validatedAt}</strong></div>}
              </div>
            )}

            <button className="enf-search-again" onClick={() => { setResult(null); setSearched(false); setQuery(''); }}>
              Search Another Plate
            </button>
          </div>
        )}
      </div>

      <div className="enf-footer">
        <Link to="/enforcement/scan">Scan license plate with camera</Link>
        <Link to="/spacemonkey/dashboard">Owner Dashboard</Link>
      </div>
    </div>
  );
};

export default EnforcementPage;
